import { useEffect, useState } from 'react';
import { LogOut, RefreshCw } from 'lucide-react';
import { api, ApiError, formatMoney, send } from '../../api';
import { Button } from '../ui/button';
import { ConfirmDialog } from '../ui/side-sheet';
import type { MenuItem } from '../ui/menu';
import bankLogo from '../../assets/logos/bank.svg';
import { EnvironmentBadge, IntegrationCard, type CardMessage, type Fact, BlockedAction } from './IntegrationCard';
import { relativeTime, toneLabel, type Health } from './model';
import type { BankView } from '../../../../src/application/bank/view';

const message = (e: unknown) => e instanceof Error ? e.message : 'This step could not be completed.';
const reassurance = 'Read-only: Alder reads transactions and never moves money. Matches are suggestions; nothing is applied.';
const linkScript = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js';

interface PlaidHandler { open(): void; destroy(): void }
declare global {
  interface Window { Plaid?: { create(config: { token: string; receivedRedirectUri?: string; onSuccess: (publicToken: string) => void;
    onExit: (error: { error_message?: string } | null) => void }): PlaidHandler } }
}
/**
 * A bank's own sign-in (OAuth) can return the browser to PLAID_REDIRECT_URI with ?oauth_state_id=…; Link must then be
 * reopened with the same Link token, so it is kept for this tab only while the bank's page is open.
 */
const tokenKey = 'alder:plaid-link-token';
const returningFromBank = () => new URLSearchParams(window.location.search).has('oauth_state_id');
const clearReturn = () => { sessionStorage.removeItem(tokenKey); window.history.replaceState(null, '', `${window.location.pathname}#integrations`); };
/** Plaid Link runs from Plaid's own script, loaded only when an owner chooses Connect. */
function loadLink(): Promise<void> {
  if (window.Plaid) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = linkScript; script.async = true;
    script.onload = () => resolve(); script.onerror = () => reject(new Error('Plaid Link could not load. Check the network and try again.'));
    document.head.appendChild(script);
  });
}
const health = (bank: BankView): Health => {
  const tone = !bank.connection ? 'off' : bank.connection.status === 'active' ? 'connected' : 'attention';
  const detail = bank.connection?.status === 'login_required' ? 'The bank asks for a fresh sign-in. Disconnect and connect again.'
    : bank.connection?.status === 'error' ? `Plaid reported ${bank.connection.lastError ?? 'an error'}.` : null;
  return { tone, label: toneLabel[tone], detail };
};

/**
 * The bank feed through Plaid: Connect opens Plaid Link in this page, the public token is exchanged on the server, and the
 * card lists recent money in with the invoices Amy thinks each payment covers.
 */
export function BankCard() {
  const [bank, setBank] = useState<BankView | null>(null); const [loadError, setLoadError] = useState('');
  const [busy, setBusy] = useState(false); const [result, setResult] = useState<CardMessage>(null);
  const [confirming, setConfirming] = useState(false);
  const reload = async () => {
    try { setBank(await api<BankView>('/app-api/bank')); setLoadError(''); } catch (e) { setLoadError(message(e)); }
  };
  useEffect(() => {
    void reload();
    const stored = sessionStorage.getItem(tokenKey);
    if (returningFromBank() && stored) void openLink(stored, window.location.href);
    else if (returningFromBank()) clearReturn();
  }, []);

  async function connect() {
    setBusy(true); setResult(null);
    try {
      const { linkToken } = await api<{ linkToken: string }>('/app-api/bank/plaid/link-token', {});
      await openLink(linkToken);
    } catch (e) { setResult({ kind: 'error', text: message(e) }); setBusy(false); }
  }
  async function openLink(linkToken: string, receivedRedirectUri?: string) {
    setBusy(true);
    try {
      await loadLink();
      sessionStorage.setItem(tokenKey, linkToken);
      const finish = () => { handler.destroy(); clearReturn(); setBusy(false); };
      const handler = window.Plaid!.create({ token: linkToken, ...(receivedRedirectUri ? { receivedRedirectUri } : {}),
        onSuccess: publicToken => void (async () => {
          try {
            const done = await api<{ bank: BankView; summary: { upserted: number } }>('/app-api/bank/plaid/exchange', { publicToken });
            setBank(done.bank);
            setResult({ kind: 'success', text: done.summary.upserted ? `Bank connected. Read ${done.summary.upserted} transactions.`
              : 'Bank connected. Plaid is still preparing its history; transactions appear here within a few minutes.' });
          } catch (e) { setResult({ kind: 'error', text: message(e) }); }
          finally { finish(); }
        })(),
        onExit: error => { finish(); if (error?.error_message) setResult({ kind: 'error', text: error.error_message }); } });
      try { handler.open(); } catch (e) { handler.destroy(); throw e; }
    } catch (e) { clearReturn(); setResult({ kind: 'error', text: message(e) }); setBusy(false); }
  }
  async function syncNow() {
    setBusy(true); setResult(null);
    try { const done = await api<{ bank: BankView }>('/app-api/bank/sync', {}); setBank(done.bank); }
    catch (e) { setResult({ kind: 'error', text: e instanceof ApiError && e.status === 409 ? 'Sync did not run: a sync is already running, or the bank needs attention.' : message(e) }); await reload(); }
    finally { setBusy(false); }
  }
  async function disconnect() {
    setBusy(true);
    try {
      const done = await send<{ bank: BankView; removedAtPlaid: boolean }>('/app-api/bank', 'DELETE'); setBank(done.bank);
      setResult({ kind: 'success', text: done.removedAtPlaid ? 'Bank disconnected. Its access was revoked at Plaid and deleted here.'
        : 'Bank disconnected here. Plaid did not confirm the removal; revoke it in the Plaid dashboard too.' });
    } catch (e) { setResult({ kind: 'error', text: message(e) }); } finally { setConfirming(false); setBusy(false); }
  }

  if (loadError) return <div className="alert error" role="alert">Bank: {loadError}</div>;
  if (!bank) return null;
  const connection = bank.connection;
  const facts: Fact[] = [{ label: 'Environment', value: <EnvironmentBadge environment={bank.environment}/> }];
  const keyFacts: Fact[] = [];
  if (connection) {
    keyFacts.push({ label: 'Bank', value: connection.institution ?? 'Connected bank' });
    keyFacts.push({ label: 'Accounts', value: connection.accounts.map(a => `${a.name}${a.mask ? ` ••${a.mask}` : ''}`).join(', ') || 'None shared' });
  }
  const freshness = connection ? { text: connection.lastSyncedAt ? `Updated ${relativeTime(connection.lastSyncedAt)}` : 'Not read yet',
    stale: false, reason: null } : null;
  const badge = connection && bank.environment === 'sandbox'
    ? { label: 'Sandbox', text: 'Plaid sandbox: test transactions, not your bank account.' } : null;
  const summary = connection ? 'Alder reads money coming in and suggests which invoices each payment covers.'
    : bank.configured ? 'Connect your bank so Alder sees payments arrive and matches them to invoices. About 3 minutes through Plaid.'
      : 'Alder sees payments arrive and matches them to invoices.';
  const primary = !bank.configured ? <BlockedAction/> : !connection
    ? <Button size="sm" disabled={busy} onClick={() => void connect()}>{busy ? 'Opening Plaid…' : 'Connect bank'}</Button> : null;
  const menu: MenuItem[] = connection ? [
    { label: 'Sync now', icon: <RefreshCw size={15} aria-hidden/>, onSelect: () => void syncNow(), disabled: busy },
    { label: 'Disconnect', icon: <LogOut size={15} aria-hidden/>, danger: true, onSelect: () => setConfirming(true) },
  ] : [];
  // Not set up on this host is not the user's error: the card says so calmly and shows the blocked state, not a red box.
  const shown: CardMessage = result;

  return <>
    <IntegrationCard id="bank" logo={bankLogo} name="Bank account" role="Bank feed via Plaid · read-only" health={health(bank)}
      summary={summary} freshness={freshness} badge={badge} keyFacts={keyFacts} facts={facts} reassurance={reassurance} message={shown} primary={primary} menu={menu}>
      {connection && <div className="bank-inbound">
        <h4>Money in</h4>
        {bank.inbound.length === 0 ? <p className="muted">No incoming payments read yet.</p> : <ul>
          {bank.inbound.map(line => <li key={line.id}>
            <span className="bank-line">{line.postedOn} · <strong>{formatMoney(line.amountMinor, line.currency)}</strong>
              {line.pending && <em> pending</em>} · {line.payer ?? line.description}</span>
            <span className="bank-match">{line.suggestions[0]
              ? `${line.suggestions[0].strength === 'strong' ? 'Likely' : 'Maybe'}: ${line.suggestions[0].customerName}`
                + (line.suggestions[0].invoiceNumbers.length ? ` · ${line.suggestions[0].invoiceNumbers.join(', ')}` : ' · amount differs')
              : line.sameAmount > 1 ? `${line.sameAmount} open invoices have this amount` : 'No match yet'}</span>
          </li>)}
        </ul>}
      </div>}
    </IntegrationCard>
    <ConfirmDialog open={confirming} busy={busy} title="Disconnect the bank?" confirmLabel="Disconnect bank"
      body="Alder stops reading the bank, revokes its access at Plaid and deletes the stored token. Lines already read stay in Alder."
      onConfirm={() => void disconnect()} onClose={() => setConfirming(false)}/>
  </>;
}
